'use client';

import { useState, useMemo, useCallback } from 'react';
import { useParams, useSearchParams, usePathname } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import { colors } from '@startengine/design-system.theme.main';
import { builder } from '@builder.io/react';

import useGetOffering from 'hooks/useGetOffering';
import { COLORS } from '../../designTokens';
import { sectionBorderRadiusSx } from '../shared/sectionBorderRadius';
import { isTtwOffering } from '../shared/offeringStatus';
import useIsHydrated from '../shared/useIsHydrated';
import useIsEditorialReskin from '../shared/useIsEditorialReskin';
import EditorialInvestSidebar from '../../editorial/EditorialInvestSidebar';
import { se3Colors } from '@startengine/design-system.se3.tokens';
import useIsMobile from 'hooks/useIsMobile';
import useIsSignedIn from 'hooks/useIsSignedIn';
import useLoginModal from 'hooks/useLoginModal';
import useOfferingDaysLeft from 'hooks/useOfferingDaysLeft';
import useGetCampaignPageCMSData from 'hooks/useGetCampaignPageCMSData';
import useExcludeAmountOfferingList from 'hooks/useExcludeAmountOfferingList';
import useRegDExcludedList from 'hooks/useRegDExcludedList';
import useShowAmountRaisedList from 'hooks/useShowAmountRaisedList';
import useCanadianFeatureFlag from 'hooks/useCanadianFeatureFlag';
import CriteriaBadge from 'components/Offering/HeroSection/CriteriaBadge/CriteriaBadge';
import SEContact from 'components/Offering/HeroSection/SEContact';
import EditorialScheduleCall from '../../editorial/EditorialScheduleCall';
import RequestLiveStreamCard from 'components/Offering/RequestLiveStreamCard';
import useGetUser from 'hooks/useGetUser';
import usePotentiallyAccredited from 'hooks/usePotentiallyAccredited';
import { isGoldCampaignSlug } from 'utils/goldCampaign';
import useGoldMeetingUrl from 'hooks/useGoldMeetingUrl';
import { getAmountRaised, getAudienceRewards } from 'services/offerings';
import useGetRewards from 'hooks/useGetRewards';
import {
  formatCurrencyFlexible,
  getEnv,
  nFormatter,
  isStage,
  imagePath,
  hasUserSeIraAccount,
  isRetirementFundsOnly,
} from 'utils/helpers';
import {
  getAmountRaisedValue,
  getNumberOfInvestors,
  isOverAmountRaised,
  resolveCurrentAmountRaised,
} from 'utils/offerings/helpers';
import {
  investNowClick,
  trackEvent,
  trackSEEvent,
  getInvestUrlWithUtm,
  ADD_TO_CART_SURFACES,
} from 'utils/analytics';
import { INVESTOR_SIGNALS_BEHAVIORAL_EVENTS } from 'utils/investorSignalsBehavioralProperties';
import { userMenu } from 'utils/links';
import { LEGACY_RESERVATION_LIST } from 'utils/constants/strings';
import { CustomTooltip } from 'components/Offering/styles';
import { useBuilderABTest } from '../../BuilderABTestContext';

// ── Styled components matching discover's CompanyComps + InvestComponent ──

// Outer card (= CompanyCompsBox) – gradient bg, rounded, shadow
const SidebarContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  marginTop: 20,
  marginBottom: 40,
  marginLeft: 'auto',
  marginRight: 'auto',
  fontFamily: "'IBM Plex Sans', sans-serif",
  [theme.breakpoints.up('sm')]: {
    marginTop: 0,
    marginBottom: 0,
    background: 'linear-gradient(64.45deg, #FFFFFF 16.05%, #efeff0 69.18%)',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.06), 0px 1px 4px rgba(0, 0, 0, 0.04)',
    borderRadius: '16px',
    backdropFilter: 'blur(2px)',
    width: '369px',
    zIndex: 5,
  },
}));

// White card around the CTA button (= InvestWrapper)
const InvestButtonWrapper = styled(Box)(({ theme }) => ({
  background: '#FFFFFF',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: '20px 0',
  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.up('sm')]: {
    boxShadow:
      '0px 24.33px 17.5728px rgba(0, 0, 0, 0.0390953), 0px 10.11px 7.29885px rgba(0, 0, 0, 0.03), 0px 3.66px 2.63985px rgba(0, 0, 0, 0.0209047)',
    borderRadius: '16px',
    padding: '20px',
    width: 'auto',
  },
}));

// Stats grid (= CompsGrid) – sits below InvestWrapper inside the gradient card
const CompsGrid = styled(Grid)(({ theme }) => ({
  ...theme.typography.p1,
  textTransform: 'uppercase',
  color: '#999',
  letterSpacing: '2px',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    background: 'none',
    boxShadow: 'none',
    backdropFilter: 'none',
    borderRadius: 0,
    padding: '40px 0px 30px 40px',
  },
  background: 'linear-gradient(64.45deg, #FFFFFF 16.05%, #efeff0 69.18%)',
  boxShadow:
    '0px 99px 59px rgba(0, 0, 0, 0.05), 0px 36.1366px 21.536px rgba(0, 0, 0, 0.0344991), 0px 17.5437px 10.4553px rgba(0, 0, 0, 0.0278145), 0px 8.60023px 5.12539px rgba(0, 0, 0, 0.0221855), 0px 3.40054px 2.02659px rgba(0, 0, 0, 0.0155009)',
  borderRadius: '16px',
  marginLeft: 'auto',
  marginRight: 'auto',
}));

const StatsBottomGrid = styled(Grid)(({ theme }) => ({
  ...theme.typography.p1,
  textTransform: 'uppercase',
  padding: '20px 16px 20px',
  color: '#999',
  letterSpacing: '2px',
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    background: 'none',
    boxShadow: 'none',
    backdropFilter: 'none',
    borderRadius: 0,
    padding: '0 !important',
  },
}));

const StatValue = styled(Typography)(({ theme }) => ({
  ...theme.typography.h7,
  color: colors.SEDark,
}));

const InvestButton = styled('button')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  maxWidth: '327px',
  height: 'auto',
  padding: '13px 16px',
  border: 'none',
  borderRadius: '16px',
  fontSize: '16px',
  fontWeight: 600,
  fontFamily: "'IBM Plex Sans', sans-serif",
  color: '#FFFFFF',
  cursor: 'pointer',
  textTransform: 'none',
  transition: 'background-color 0.2s',
  '&:hover': {
    opacity: 0.95,
  },
}));

const ClosedButton = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  maxWidth: '327px',
  height: 'auto',
  padding: '13px 16px',
  borderRadius: '16px',
  fontSize: '16px',
  fontWeight: 600,
  fontFamily: "'IBM Plex Sans', sans-serif",
  color: '#FFFFFF',
  backgroundColor: '#999',
}));

const LoyaltyBanner = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  background: COLORS.accent,
  borderRadius: '8px 8px 0 0',
  padding: '12px 16px',
  gap: '12px',
}));

const getCTAText = (offeringData) => {
  if (!offeringData) return 'Invest Now';
  if (offeringData.investmentSourceOfFunds === 'RETIREMENT_FUNDS_ONLY') {
    return 'Invest with IRA';
  }
  if (offeringData.offeringType === 'equity') return 'Get Equity';
  return 'Invest Now';
};

const BuilderClassicInvestSidebar = ({
  sectionId = '',
  sectionBorderRadius = '0px',
  showLoyaltyBonus = true,
  showStats = true,
  showRequestLiveStream = true,
  showInvestButton: showInvestButtonProp = true,
  investButtonColor = '',
  waitlistButtonColor = '',
  forceReserveCta = false,
}) => {
  const params = useParams();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const isMobile = useIsMobile();
  const slug = params?.slug?.[0];
  const abTest = useBuilderABTest();

  const isBuilderEditing =
    builder?.isEditing ||
    builder?.isPreviewing ||
    searchParams?.get('__builder_editing__') === 'true' ||
    searchParams?.has('builder.frameEditing');

  const { data: offeringData, isLoading: isLoadingOffering } = useGetOffering({ slug });
  const user = useSelector((state) => state.user);
  const userAccounts = useSelector((state) => state.user.userAccounts);
  const investmentAccountStatus = useSelector((state) => state.user.investmentAccountStatus);
  const primaryUserId = useSelector((state) => state.user.primaryUserId);
  const {
    needToLogin,
    verifiedPrimaryUserId,
    loading: isPartnerLoading,
  } = useSelector((state) => state.partnerVerification);
  const signedIn = useIsSignedIn();
  const [renderLoginModal, setLoginModalOpen] = useLoginModal();
  const [getTillOfferingClose] = useOfferingDaysLeft();
  const { isEnabled: isCanadianFeatureEnabled } = useCanadianFeatureFlag();
  const isCanadianRoute = isCanadianFeatureEnabled && pathname?.startsWith('/private-canada/');

  const partner = searchParams?.get('partner');
  const searchParamConnectRequestId = searchParams?.get('connectRequestId');
  const connectRequestId = searchParamConnectRequestId?.replaceAll('/', '');

  const { hideTerms } = useGetCampaignPageCMSData({ slug });
  const { data: REGD_EXCLUDED_LIST, isSuccess: loadedRegD } = useRegDExcludedList();
  const { data: EXCLUDE_AMOUNT_OFFERING_LIST, isSuccess: loadedExcluded } =
    useExcludeAmountOfferingList();
  const { data: SHOW_AMOUNT_RAISED_LIST } = useShowAmountRaisedList();

  // PD-34457 follow-up: the V2 facade emits four distinct status values
  // for TTW (`testing`, `test_the_waters`, `pre_launch`, `ttw`). Checking
  // only `'testing'` left Reg A+ TTW pages (e.g. `greenfield-robotics-rega`)
  // showing "Get Equity" with the orange Reserve Now button suppressed,
  // because their status is typically `'test_the_waters'` or `'pre_launch'`.
  // `isTtwOffering` excludes Reg D — those use `not_submitted` to mean
  // "pre-launch private offering", not TTW. Without this, Consensys
  // (Reg D + not_submitted) rendered the orange "Reserve Now" button
  // instead of "Get Equity" (Hannah Minn report, Jun 2 2026).
  //
  // `not_submitted` is *not* auto-detected as TTW because the V2 payload
  // can't tell a real pre-Form-C / pre-qualification TTW campaign apart
  // from an unfinished draft preview (heartstone-cf-2 vs gacw-reg-a, both
  // come back identical). Issuers / admins flip the `forceReserveCta`
  // toggle in Builder.io to opt the sidebar into Reserve Now mode for
  // those offerings — see `shared/offeringStatus.js` for the rationale.
  const isTTW = isTtwOffering(offeringData) || Boolean(forceReserveCta);
  const isRegD =
    offeringData?.regulationType === 'Reg D' || offeringData?.regulationType === '506c';
  // IRA offerings are private offerings too, but their legacy payloads do not
  // consistently identify the regulation as the exact `Reg D` / `506c`
  // values above. Keep their editorial sidebar support card on the shared SE3
  // treatment rather than falling through to the legacy schedule-card path.
  // Some legacy IRA payloads do not include `investmentSourceOfFunds`. The
  // route slug is an intentional fallback for those named IRA campaigns so the
  // SE3 sidebar continues to render the IRA support/schedule-call treatment.
  const isIraOffering =
    isRetirementFundsOnly(offeringData) || /(?:^|-)ira(?:-|$)/i.test(offeringData?.slug || '');
  const showTerms = !!hideTerms || !isTTW;

  const { data: userData } = useGetUser();
  const isGoldAdmin =
    userData?.roles?.includes('ADMIN') || userData?.roles?.includes('SECONDARY_ADMIN');
  const isGoldCampaign = isGoldCampaignSlug(slug);
  const { isPotentiallyAccredited } = usePotentiallyAccredited({
    primaryUserId: userData?.primaryUserId,
    enabled: isGoldCampaign && signedIn === true && !!userData?.primaryUserId,
  });
  const showGoldMeetingCard =
    isGoldCampaign && signedIn === true && (isPotentiallyAccredited || isGoldAdmin);

  const goldMeetingUrl = useGoldMeetingUrl(showGoldMeetingCard);

  const isOverRaised = isOverAmountRaised(offeringData) || offeringData?.lockedOnWaitlist;
  const endDays = getTillOfferingClose(offeringData);
  const CTAText = getCTAText(offeringData);
  const convertibleNote = offeringData?.offeringType === 'convertible_note';

  const shouldShowAmount =
    loadedExcluded &&
    SHOW_AMOUNT_RAISED_LIST?.includes(offeringData?.slug) &&
    !EXCLUDE_AMOUNT_OFFERING_LIST?.includes(offeringData?.slug);

  // Amount raised / investor count are financial display data; always refetch
  // and prefer this endpoint over the initial offering payload.
  const { data: amountRaisedObject, isLoading: isLoadingAmountRaised } = useQuery({
    queryKey: ['amountRaised', offeringData?.id],
    queryFn: () => getAmountRaised({ offeringId: offeringData?.id }),
    enabled: Boolean(offeringData?.id) && !LEGACY_RESERVATION_LIST.includes(offeringData?.slug),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const currentAmountRaised = resolveCurrentAmountRaised({
    amountRaisedObject,
    offeringData,
  });
  const currentAmountRaisedNumber = Number.parseFloat(currentAmountRaised || 0);

  const regDExclusion = loadedRegD ? !REGD_EXCLUDED_LIST?.includes(offeringData?.slug) : true;
  const hideValue = loadedExcluded
    ? EXCLUDE_AMOUNT_OFFERING_LIST?.includes(offeringData?.slug)
    : true;
  const hideRaised =
    (offeringData?.regulationType === 'Reg D' && regDExclusion) ||
    offeringData?.regulationType === '506(c)' ||
    currentAmountRaisedNumber < 124000;
  const hideInvestors = hideRaised && regDExclusion;

  // Audience rewards
  const { data: audienceRewards } = useQuery({
    queryKey: ['audienceRewards', offeringData?.id, primaryUserId],
    queryFn: () => getAudienceRewards(offeringData?.id, primaryUserId),
    enabled: Boolean(signedIn && offeringData?.id && primaryUserId),
    staleTime: 5 * 60 * 1000,
  });

  const { data: rewardData } = useGetRewards({ offeringId: offeringData?.id });
  const _hasRewards = rewardData?.length > 0;

  // Invest URL + click handler
  const [investUrl, setInvestUrl] = useState('');

  const getRerouteUrl = useCallback(
    (isReserved) => {
      if (investmentAccountStatus?.investmentAccountFrozen) return userMenu.portfolio;
      let url = '';
      if (LEGACY_RESERVATION_LIST.includes(offeringData?.slug)) {
        url = `${process.env.NEXT_PUBLIC_ROOT_URL}/${slug}/reserve`;
      } else if (isReserved) {
        url = `${getEnv()}investment.startengine.com/${slug}/reserve`;
      } else {
        url = `${getEnv()}investment.startengine.com/${slug}`;
      }
      if (partner && connectRequestId) {
        url = `${url}${url.includes('?') ? '&' : '?'}partner=${partner}&connectRequestId=${connectRequestId}`;
      }
      return url;
    },
    [offeringData?.slug, slug, partner, connectRequestId, investmentAccountStatus],
  );

  // Shared invest/reserve click analytics; only the AB-test section name differs.
  // Fire-and-forget: the upstream trackers swallow their own errors, so awaiting
  // them only delays navigation. Kept synchronous so the click path never blocks
  // on a network round-trip.
  const trackInvestClickAnalytics = useCallback(
    (sectionName) => {
      if (!offeringData) return;
      investNowClick({ offeringData, user, surface: ADD_TO_CART_SURFACES.CAMPAIGN_PAGE });
      trackSEEvent(INVESTOR_SIGNALS_BEHAVIORAL_EVENTS.clickInvestNow, offeringData?.slug, user);
      trackEvent(INVESTOR_SIGNALS_BEHAVIORAL_EVENTS.clickInvestNow, offeringData);
      abTest?.trackInvestClick({ offering_slug: slug, section_name: sectionName });
    },
    [offeringData, user, slug, abTest],
  );

  const handleInvestClick = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (offeringData?.status === 'not_submitted') return;
      if (investmentAccountStatus?.investmentAccountFrozen) {
        window.location.href = userMenu.portfolio;
        return;
      }
      if (isCanadianRoute) {
        window.location.href = `/private-canada/invest?offering=${slug}`;
        return;
      }

      const url = getRerouteUrl(false);

      trackInvestClickAnalytics('classic_sidebar');

      if (!user?.signedIn || (needToLogin && !isPartnerLoading)) {
        setInvestUrl(url);
        setLoginModalOpen(true);
        return;
      }

      window.location.href = getInvestUrlWithUtm(
        `${url}${window.location.search ? `${window.location.search}` : ''}`,
        slug,
      );
    },
    [
      offeringData,
      investmentAccountStatus,
      isCanadianRoute,
      slug,
      getRerouteUrl,
      user,
      needToLogin,
      isPartnerLoading,
      setLoginModalOpen,
      trackInvestClickAnalytics,
    ],
  );

  const handleReserveClick = useCallback(
    async (e) => {
      e?.preventDefault?.();
      const url = getRerouteUrl(true);
      trackInvestClickAnalytics('classic_sidebar_reserve');
      if (!user?.signedIn || (needToLogin && !isPartnerLoading)) {
        setInvestUrl(url);
        setLoginModalOpen(true);
        return;
      }
      window.location.href = `${url}${window.location.search || ''}`;
    },
    [
      getRerouteUrl,
      user,
      needToLogin,
      isPartnerLoading,
      setLoginModalOpen,
      trackInvestClickAnalytics,
    ],
  );

  // Previous raise total
  const getRaisedTotalValue = useMemo(() => {
    if (!offeringData?.previousRaiseTotal && !offeringData?.previousCrowdfundedRaiseTotal)
      return '';
    const value =
      Number.parseFloat(offeringData?.previousRaiseTotal || 0) +
      Number.parseFloat(offeringData?.previousCrowdfundedRaiseTotal || 0);
    if (!value || value <= 0) return '';
    return formatCurrencyFlexible(value);
  }, [offeringData?.previousRaiseTotal, offeringData?.previousCrowdfundedRaiseTotal]);

  const resolvedInvestColor = investButtonColor || colors.SEDark;
  const resolvedWaitlistColor = waitlistButtonColor || colors.SEOrange;

  const raisedTooltipText = useMemo(() => {
    if (!offeringData?.name) return '';
    const maxFunding = offeringData?.maximumFundingAmount
      ? formatCurrencyFlexible(Number.parseFloat(offeringData.maximumFundingAmount))
      : '';
    const fundingGoal = offeringData?.fundingGoal
      ? formatCurrencyFlexible(Number.parseFloat(offeringData.fundingGoal))
      : '';
    return `${offeringData.name} is raising up to ${maxFunding} with a minimum goal of ${fundingGoal}. The amount raised may include insider investments, which may go toward meeting the minimum offering amount.`;
  }, [offeringData?.name, offeringData?.maximumFundingAmount, offeringData?.fundingGoal]);

  const minInvestTooltipText = useMemo(
    () =>
      offeringData?.name
        ? `The smallest investment amount that ${offeringData.name} is accepting.`
        : '',
    [offeringData?.name],
  );

  const isHydrated = useIsHydrated();
  const isEditorialReskin = useIsEditorialReskin();

  if (!isHydrated) return null;

  if (isLoadingOffering || !offeringData) {
    if (!isBuilderEditing) {
      return (
        <SidebarContainer data-cy="builder-classic-invest-sidebar-skeleton">
          <CompsGrid container rowSpacing={2}>
            <Grid item xs={12}>
              <Skeleton variant="rectangular" height={60} sx={{ borderRadius: '4px' }} />
            </Grid>
            <Grid item xs={7}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="80%" />
            </Grid>
            <Grid item xs={5}>
              <Skeleton variant="text" width="50%" />
              <Skeleton variant="text" width="70%" />
            </Grid>
          </CompsGrid>
        </SidebarContainer>
      );
    }
  }

  const showInvestButton =
    showInvestButtonProp &&
    Boolean(offeringData?.status) &&
    offeringData.status !== 'closed' &&
    offeringData.status !== 'completed' &&
    !isTTW;

  const showValuationCap =
    offeringData?.valuationCap &&
    offeringData?.valuationCap !== '0.00 USD' &&
    offeringData?.offeringType !== 'equity' &&
    !isTTW;

  // Pre-compute each row's visibility so we can avoid mounting the
  // empty `CompsGrid` shell entirely when nothing inside would render.
  // Without this, TTW pages (especially Reg CF `not_submitted` where
  // `showTerms` is false and there's no raise data yet) ship a visible
  // drop-shadowed white card under the Reserve Now button with no
  // content — looks like a layout bug. Mirrors each `&&` guard in the
  // JSX below; keep in sync if a new row is added.
  const showRaisedRow =
    (!hideRaised && !hideValue) ||
    (isStage && /automation|auto/.test(slug) && !hideValue) ||
    shouldShowAmount;
  const showPreviouslyCrowdfunded = Boolean(getRaisedTotalValue) && showRaisedRow;
  const showInvestorsRow =
    shouldShowAmount || (!hideInvestors && !hideValue && currentAmountRaisedNumber >= 124000);
  const showMinInvestRow = showTerms;
  const showValuationRow = !showValuationCap && showTerms && !isRegD;
  const showFundingGoalRow =
    !showValuationCap && isRegD && ['approved', 'testing'].includes(offeringData?.status);
  const anyStatVisible =
    showPreviouslyCrowdfunded ||
    showRaisedRow ||
    showInvestorsRow ||
    showMinInvestRow ||
    showValuationCap ||
    showValuationRow ||
    showFundingGoalRow;

  // The stats are normally rendered as 2-column pairs (RAISED/INVESTORS,
  // MIN INVEST/VALUATION). On Reg D pages most rows are hidden by the
  // exclusion rules above, so the surviving stat sits in its left
  // half (xs=7 md=6) leaving a visible blank in the right half — looks
  // like a layout bug (Hannah Minn report Jun 2 2026 — Consensys mobile
  // showed "MIN INVEST $15,000" with empty white space to the right
  // of it). When a stat's right-side pair is hidden, expand it to the
  // full row width so the card content visually fills the slot.
  const raisedRightPairVisible = showInvestorsRow;
  const minInvestRightPairVisible = showValuationCap || showValuationRow || showFundingGoalRow;
  const raisedRowXs = raisedRightPairVisible ? { xs: 7, md: 6 } : { xs: 12, md: 12 };
  const minInvestRowXs = minInvestRightPairVisible ? { xs: 7, md: 6 } : { xs: 12, md: 12 };

  if (isEditorialReskin) {
    const ctaText = isOverRaised ? 'Join the Waitlist' : CTAText;
    const ctaSubline =
      showTerms &&
      !convertibleNote &&
      offeringData?.offeringType !== 'safe' &&
      offeringData?.pricePerShare
        ? `${formatCurrencyFlexible(Number.parseFloat(offeringData.pricePerShare))} Per Share`
        : null;

    const editorialStats = [];
    if (showPreviouslyCrowdfunded) {
      editorialStats.push({
        key: 'prev-crowdfunded',
        label: 'Previously Crowdfunded',
        value: getRaisedTotalValue,
        fullWidth: true,
        labelDataCy: 'editorial-sidebar-prev-crowdfunded-label',
        valueDataCy: 'editorial-sidebar-prev-crowdfunded-amount',
        labelColor: '#856528',
        tooltip:
          'Previous Reg A+ and Reg CF offerings. This includes offerings conducted on other platforms and on different terms from the current offering. Please see the offering document for additional information.',
      });
    }
    if (showRaisedRow) {
      editorialStats.push({
        key: 'raised',
        label: isTTW ? 'Reserved' : 'Raised',
        value: getAmountRaisedValue({
          isTTW,
          amountRaisedObject,
          offeringData,
          showRaisedText: false,
        }),
        tooltip: raisedTooltipText,
        valueDataCy: 'editorial-sidebar-raised-amount',
      });
    }
    if (showInvestorsRow) {
      editorialStats.push({
        key: 'investors',
        label: isTTW ? 'Reservations' : 'Investors',
        value: getNumberOfInvestors({
          isTTW,
          amountRaisedObject,
          offeringData,
          showRaisedText: false,
        }),
        valueDataCy: 'editorial-sidebar-investor-count',
      });
    }
    if (showMinInvestRow) {
      editorialStats.push({
        key: 'min-invest',
        label: 'Min Invest',
        value:
          offeringData?.minimumInvestmentAmount &&
          formatCurrencyFlexible(Number.parseFloat(offeringData.minimumInvestmentAmount)),
        tooltip: minInvestTooltipText,
        labelDataCy: 'editorial-sidebar-min-invest-label',
        valueDataCy: 'editorial-sidebar-min-invest',
      });
    }
    if (showValuationCap) {
      editorialStats.push({
        key: 'valuation-cap',
        label: 'Valuation Cap',
        value: `$${nFormatter(Number.parseFloat(offeringData.valuationCap), 2)}`,
        valueDataCy: 'editorial-sidebar-valuation-cap',
      });
    } else if (showValuationRow) {
      editorialStats.push({
        key: 'valuation',
        label: 'Valuation',
        value:
          offeringData?.valuation && `$${nFormatter(Number.parseFloat(offeringData.valuation), 2)}`,
        valueDataCy: 'editorial-sidebar-valuation',
      });
    } else if (showFundingGoalRow) {
      editorialStats.push({
        key: 'funding-goal',
        label: 'Funding Goal',
        value:
          offeringData?.maximumFundingAmount &&
          `$${nFormatter(Number.parseFloat(offeringData.maximumFundingAmount), 2)}`,
        valueDataCy: 'editorial-sidebar-funding-goal',
      });
    }

    const BADGE_COPY = {
      MOST_MOMENTUM: {
        title: 'Most Momentum',
        description: 'Top 15 in amount raised last 72 hours',
        showDisclaimer: true,
      },
      MOST_POPULAR: {
        title: 'Most Funded',
        description: 'Top 15 in amount raised on StartEngine',
        showDisclaimer: true,
      },
    };
    const editorialBadges = (offeringData?.badges || [])
      .filter((b) => b?.name === 'MOST_MOMENTUM' || b?.name === 'MOST_POPULAR')
      .map((b) => BADGE_COPY[b.name])
      .filter(Boolean);

    // Keep the compact eligibility pill in the SE3 hero, while also repeating
    // the restriction beside the investment snapshot where investors make the
    // decision to continue. This mirrors the control layout's criteria card
    // and makes the restriction harder to miss before entering the funnel.
    if (offeringData?.internationalInvestorsAllowed === false) {
      editorialBadges.unshift({
        title: 'US Investors Only',
        description: 'This offering is open only to US investors',
        showDisclaimer: false,
      });
    }

    const showReserveEditorialCta = showInvestButtonProp && isTTW;
    const showScheduleCall =
      !isMobile &&
      ((showGoldMeetingCard && goldMeetingUrl) ||
        ((isRegD || isIraOffering) && !(showGoldMeetingCard && goldMeetingUrl)));

    return (
      <>
        <Box
          id={sectionId || undefined}
          sx={sectionBorderRadiusSx(sectionBorderRadius, { clip: false })}
          data-cy="builder-classic-invest-sidebar"
        >
          <EditorialInvestSidebar
            showCta={showInvestButton || showReserveEditorialCta}
            closed={offeringData?.status === 'closed'}
            ctaText={showReserveEditorialCta ? 'Reserve Now' : ctaText}
            ctaSubline={showReserveEditorialCta ? null : ctaSubline}
            ctaColor={
              // eslint-disable-next-line no-nested-ternary
              showReserveEditorialCta
                ? se3Colors.seOrange
                : isOverRaised
                  ? resolvedWaitlistColor
                  : // PD (Paige QA): match the editorial deep teal used by the hero CTA
                    // (seDark #185C65), not the brighter tealInk (#0C6F64).
                    se3Colors.seDark
            }
            ctaHref={getRerouteUrl(showReserveEditorialCta)}
            ctaDisabled={offeringData?.status === 'not_submitted'}
            onCtaClick={showReserveEditorialCta ? handleReserveClick : handleInvestClick}
            stats={editorialStats}
            badges={editorialBadges}
            iraDisclaimer={
              isIraOffering && !hasUserSeIraAccount(userAccounts) ? (
                <Box
                  sx={{
                    mt: 2,
                    textAlign: 'center',
                    fontFamily: "'IBM Plex Sans', sans-serif",
                  }}
                  data-cy="editorial-sidebar-ira-disclaimer"
                >
                  <Box sx={{ fontSize: '14px', fontWeight: 600, color: se3Colors.tealInk }}>
                    You can open an IRA with StartEngine
                  </Box>
                  <Box
                    sx={{
                      fontSize: '9px',
                      color: se3Colors.ink3,
                      lineHeight: 1.4,
                      mt: 0.5,
                      '& a': { color: se3Colors.tealInk, textDecoration: 'underline' },
                    }}
                  >
                    Equity Trust Company is the custodian of StartEngine IRAs. StartEngine
                    Crowdfunding, Inc. covers IRA account fees if you invest $20,000 or more per
                    year; otherwise, you are responsible for all applicable fees annually. See{' '}
                    <a
                      href="https://s3.us-east-2.amazonaws.com/forms.equitytrustcompany.info/WealthBridge_Fee-Schedule.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      IRA Fees
                    </a>{' '}
                    for details.
                  </Box>
                </Box>
              ) : null
            }
            scheduleCall={
              showScheduleCall ? (
                <Box sx={{ width: '100%', mt: '16px' }} data-cy="editorial-sidebar-schedule-call">
                  <EditorialScheduleCall
                    calendarUrlOverride={
                      showGoldMeetingCard && goldMeetingUrl ? goldMeetingUrl : ''
                    }
                  />
                </Box>
              ) : null
            }
          />
        </Box>

        {showRequestLiveStream && (
          <Box sx={{ mt: '16px' }}>
            <RequestLiveStreamCard data-cy="classic-sidebar-request-live-stream-button" />
          </Box>
        )}

        {renderLoginModal({
          offering: offeringData,
          verifiedPrimaryUserId,
          onClose: (loginUser) => {
            if (loginUser?.event === 'signup') {
              const search = window.location.search ? `${window.location.search}` : '';
              window.location.href = `${investUrl}${search}`;
            }
          },
          onSuccessfulLogin: () => {
            investNowClick({
              offeringData,
              user,
              surface: ADD_TO_CART_SURFACES.CAMPAIGN_PAGE,
            });
            const search = window.location.search ? `${window.location.search}` : '';
            window.location.href = `${investUrl}${search}`;
          },
        })}
      </>
    );
  }

  return (
    <>
      <SidebarContainer
        id={sectionId || undefined}
        sx={sectionBorderRadiusSx(sectionBorderRadius, { clip: false })}
        data-cy="builder-classic-invest-sidebar"
      >
        {/* Loyalty bonus banner */}
        {showLoyaltyBonus && !isMobile && audienceRewards?.active && (
          <LoyaltyBanner data-cy="classic-sidebar-loyalty-bonus">
            <img
              src={`${imagePath}/images/offering/loyalty-white.svg`}
              alt="Loyalty Bonus"
              width={26}
              height={26}
            />
            <Box>
              <Typography variant="h7" sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                Earn {audienceRewards?.percentBonusShares}%{' '}
                {convertibleNote ? 'Bonus Interest' : 'Bonus Shares'}
              </Typography>
              <Typography
                variant="p3SemiBold"
                sx={{ color: '#fff', fontSize: '10px', letterSpacing: '1px' }}
              >
                LOYALTY BONUS
              </Typography>
            </Box>
          </LoyaltyBanner>
        )}

        {/* Invest / Reserve / Closed button */}
        <InvestButtonWrapper
          sx={{ mt: showLoyaltyBonus && !isMobile && audienceRewards?.active ? 0 : 0 }}
        >
          {showInvestButton && (
            <InvestButton
              onClick={handleInvestClick}
              disabled={offeringData?.status === 'not_submitted'}
              data-cy="classic-sidebar-invest-button"
              style={{
                backgroundColor: isOverRaised ? resolvedWaitlistColor : resolvedInvestColor,
              }}
            >
              {isOverRaised ? 'Join the Waitlist' : CTAText}
              {!convertibleNote &&
                offeringData?.offeringType !== 'safe' &&
                showTerms &&
                offeringData?.pricePerShare && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: 'rgba(255,255,255,0.85)',
                      mt: 0.25,
                    }}
                  >
                    {formatCurrencyFlexible(Number.parseFloat(offeringData.pricePerShare))} Per
                    Share
                  </Box>
                )}
              {offeringData?.offeringType === 'safe' && offeringData?.discountRate && (
                <Box
                  component="span"
                  sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'rgba(255,255,255,0.85)',
                    mt: 0.25,
                  }}
                >
                  {offeringData.discountRate}% Discount
                </Box>
              )}
              {convertibleNote && offeringData?.debtInterestRate && (
                <Box
                  component="span"
                  sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'rgba(255,255,255,0.85)',
                    mt: 0.25,
                  }}
                >
                  {Number.parseFloat(offeringData.debtInterestRate).toFixed(1)}% Interest Rate
                </Box>
              )}
            </InvestButton>
          )}
          {offeringData?.status === 'closed' && (
            <ClosedButton data-cy="classic-sidebar-closed-button">Offering Closed</ClosedButton>
          )}
          {showInvestButtonProp && isTTW && (
            <InvestButton
              onClick={handleReserveClick}
              data-cy="classic-sidebar-reserve-button"
              style={{ backgroundColor: '#E06004' }}
            >
              Reserve Now
            </InvestButton>
          )}
          {isIraOffering && !hasUserSeIraAccount(userAccounts) && (
            <>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: colors.SEDark,
                  textAlign: 'center',
                  mt: 1,
                }}
              >
                You can open an IRA with StartEngine
              </Typography>
              {/* Mirrors legacy `InvestComponent.jsx` IraDisclaimer paragraph
                  (HeroSection/InvestComponent.jsx:581-593). The Builder
                  classic sidebar previously only rendered the one-line
                  "You can open an IRA" subtitle, so IRA-only offerings on
                  Builder layout (e.g. PsiQuantum-IRA-1, Jun 2 2026 Hannah
                  Minn report) were missing the Equity Trust custodian
                  disclaimer that compliance requires under the CTA. */}
              <Typography
                sx={{
                  fontSize: '8px',
                  fontWeight: 400,
                  color: colors.gray1,
                  textAlign: 'center',
                  lineHeight: 1.2,
                  mt: 0.5,
                  '& a': {
                    color: colors.SEDark,
                    textDecoration: 'underline',
                  },
                }}
              >
                Equity Trust Company is the custodian of StartEngine IRAs. StartEngine Inc. covers
                IRA account fees if you invest $20,000 or more per year; otherwise, you are
                responsible for all applicable fees annually. See{' '}
                <a
                  href="https://s3.us-east-2.amazonaws.com/forms.equitytrustcompany.info/WealthBridge_Fee-Schedule.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  IRA Fees
                </a>{' '}
                for details.
              </Typography>
            </>
          )}
        </InvestButtonWrapper>

        {/* Stats section (CompsGrid > StatsBottomGrid, matching discover) */}
        {showStats && anyStatVisible && (
          <CompsGrid container rowSpacing={2}>
            <StatsBottomGrid container rowSpacing={2}>
              {/* Previously crowdfunded */}
              {showPreviouslyCrowdfunded && (
                <Grid item xs={12}>
                  <Grid item container alignItems="flex-end">
                    <Typography
                      variant="h8"
                      data-cy="classic-sidebar-prev-crowdfunded-label"
                      sx={{ color: '#856528' }}
                    >
                      PREVIOUSLY CROWDFUNDED
                    </Typography>
                    <Grid
                      item
                      sx={{
                        button: { padding: 0, margin: 0, height: 'auto', width: 'auto' },
                        '& svg': { color: '#856528' },
                      }}
                      data-cy="classic-sidebar-prev-crowdfunded-tooltip"
                    >
                      <CustomTooltip content="Previous Reg A+ and Reg CF offerings. This includes offerings conducted on other platforms and on different terms from the current offering. Please see the offering document for additional information." />
                    </Grid>
                  </Grid>
                  <StatValue data-cy="classic-sidebar-prev-crowdfunded-amount">
                    {getRaisedTotalValue}
                  </StatValue>
                </Grid>
              )}

              {/* Raised */}
              {((!hideRaised && !hideValue) ||
                (isStage && /automation|auto/.test(slug) && !hideValue) ||
                shouldShowAmount) && (
                <Grid item xs={raisedRowXs.xs} md={raisedRowXs.md}>
                  <Grid item container alignItems="flex-end">
                    <Typography variant="h8">{isTTW ? 'RESERVED' : 'RAISED'}</Typography>
                    <Grid
                      item
                      sx={{ button: { padding: 0, margin: 0, height: 'auto', width: 'auto' } }}
                      data-cy="classic-sidebar-raised-tooltip"
                    >
                      <CustomTooltip content={raisedTooltipText} />
                    </Grid>
                  </Grid>
                  <StatValue data-cy="classic-sidebar-raised-amount">
                    {isLoadingAmountRaised &&
                    !amountRaisedObject?.amountRaised &&
                    !offeringData?.amountRaised ? (
                      <Skeleton variant="text" width="60px" height="24px" />
                    ) : (
                      getAmountRaisedValue({
                        isTTW,
                        amountRaisedObject,
                        offeringData,
                        showRaisedText: false,
                      })
                    )}
                  </StatValue>
                </Grid>
              )}

              {/* Investors */}
              {(shouldShowAmount ||
                (!hideInvestors && !hideValue && currentAmountRaisedNumber >= 124000)) && (
                <Grid item xs={5} md={6} pl="6px" alignSelf="flex-end">
                  <Typography variant="h8">{isTTW ? 'RESERVATIONS' : 'INVESTORS'}</Typography>
                  <StatValue data-cy="classic-sidebar-investor-count">
                    {isLoadingAmountRaised ? (
                      <Skeleton variant="text" width="40px" height="24px" />
                    ) : (
                      getNumberOfInvestors({
                        isTTW,
                        amountRaisedObject,
                        offeringData,
                        showRaisedText: false,
                      })
                    )}
                  </StatValue>
                </Grid>
              )}

              {/* Min Invest */}
              {showTerms && (
                <Grid item xs={minInvestRowXs.xs} md={minInvestRowXs.md}>
                  <Grid item container alignItems="flex-end">
                    <Typography variant="h8" data-cy="classic-sidebar-min-invest-label">
                      MIN INVEST
                    </Typography>
                    <Grid
                      item
                      sx={{ button: { padding: 0, margin: 0, height: 'auto', width: 'auto' } }}
                      data-cy="classic-sidebar-min-invest-tooltip"
                    >
                      <CustomTooltip content={minInvestTooltipText} />
                    </Grid>
                  </Grid>
                  <StatValue data-cy="classic-sidebar-min-invest">
                    {offeringData?.minimumInvestmentAmount &&
                      formatCurrencyFlexible(
                        Number.parseFloat(offeringData.minimumInvestmentAmount),
                      )}
                  </StatValue>
                </Grid>
              )}

              {/* Valuation / Valuation Cap / Funding Goal */}
              {showValuationCap && (
                <Grid item xs={5} md={6} pl="6px" alignSelf="flex-end">
                  <Typography variant="h8">VALUATION CAP</Typography>
                  <StatValue data-cy="classic-sidebar-valuation-cap">
                    {`$${nFormatter(Number.parseFloat(offeringData.valuationCap), 2)}`}
                  </StatValue>
                </Grid>
              )}
              {!showValuationCap && showTerms && !isRegD && (
                <Grid item xs={5} md={6} pl="6px" alignSelf="flex-end">
                  <Typography variant="h8">VALUATION</Typography>
                  <StatValue data-cy="classic-sidebar-valuation">
                    {offeringData?.valuation &&
                      `$${nFormatter(Number.parseFloat(offeringData.valuation), 2)}`}
                  </StatValue>
                </Grid>
              )}
              {!showValuationCap &&
                isRegD &&
                ['approved', 'testing'].includes(offeringData?.status) && (
                  <Grid item xs={5} md={6} pl="6px" alignSelf="flex-end">
                    <Typography variant="h8">FUNDING GOAL</Typography>
                    <StatValue data-cy="classic-sidebar-funding-goal">
                      {offeringData?.maximumFundingAmount &&
                        `$${nFormatter(Number.parseFloat(offeringData.maximumFundingAmount), 2)}`}
                    </StatValue>
                  </Grid>
                )}
            </StatsBottomGrid>
          </CompsGrid>
        )}
      </SidebarContainer>

      {!isMobile && offeringData && (
        <Box
          sx={{ width: '100%', maxWidth: '370px', pt: '8px' }}
          data-cy="builder-classic-criteria-badge"
        >
          <CriteriaBadge offering={offeringData} />
        </Box>
      )}

      {/* Inline "Schedule a call with an Associate" CTA. Two render paths
          here, mirroring legacy `HeroSection/Heading.jsx:518-523`:
            1. Gold campaigns — uses the per-campaign meeting URL once the
               user is signed in and confirmed/admin-overridden as
               accredited (existing behavior).
            2. Reg D / 506(c) — every private offering should surface the
               sales-team calendar inline next to the sidebar (Hannah Minn
               report Jun 2 2026: PsiQuantum-IRA-1 and other private
               offerings on the Builder classic layout were missing this
               CTA, while legacy non-Builder Reg D pages had it).
          The floating `BuilderScheduleCallBubble` exists as a separate
          opt-in component but isn't placed on every Builder template,
          so we keep the inline render in parity with the legacy layout. */}
      {!isMobile && showGoldMeetingCard && goldMeetingUrl && (
        <Box sx={{ width: '100%', maxWidth: '370px' }}>
          <SEContact mt="16px" calendarUrlOverride={goldMeetingUrl} />
        </Box>
      )}
      {!isMobile && isRegD && !(showGoldMeetingCard && goldMeetingUrl) && (
        <Box sx={{ width: '100%', maxWidth: '370px' }}>
          <SEContact mt="16px" />
        </Box>
      )}

      {showRequestLiveStream && (
        <RequestLiveStreamCard data-cy="classic-sidebar-request-live-stream-button" />
      )}

      {renderLoginModal({
        offering: offeringData,
        verifiedPrimaryUserId,
        onClose: (loginUser) => {
          if (loginUser?.event === 'signup') {
            const search = window.location.search ? `${window.location.search}` : '';
            window.location.href = `${investUrl}${search}`;
          }
        },
        onSuccessfulLogin: () => {
          // PD-34901 — login-then-invest path. The user wasn't signed in
          // when they tapped Get Equity, so `signed_in_pre_click` is
          // false; record that distinction via a dedicated surface so the
          // readout doesn't conflate it with already-signed-in clicks.
          investNowClick({
            offeringData,
            user,
            surface: ADD_TO_CART_SURFACES.CAMPAIGN_PAGE,
          });
          const search = window.location.search ? `${window.location.search}` : '';
          window.location.href = `${investUrl}${search}`;
        },
      })}
    </>
  );
};

export default BuilderClassicInvestSidebar;
